import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { musicalWorkLinkService } from "../services/musicalWorkLinkService";
import { musicalWorkLinkDisplayId } from "./MusicalWorkLinkCommonStore";
import { MusicalWorkLink } from "../../../api/graphql";
import { platformDisplayId } from "../../platform/store/PlatformCommonStore";
import { platformLinkTypeDisplayId } from "../../platformLinkType/store/PlatformLinkTypeCommonStore";
import { platformService } from "../../platform/services/platformService";
import { platformLinkTypeService } from "../../platformLinkType/services/platformLinkTypeService";
import { InitializeParams } from "../../../store/types";

export class MusicalWorkLinkViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = musicalWorkLinkService;
  musicalWorkLinkInstance: MusicalWorkLink | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  urlValue: string = "";
  platformService = platformService;
  platformSearchValue: string = "";
  platformIdValue: string = "";
  platformSelectedValue: { displayId: string; id: string } | null = null;
  typeService = platformLinkTypeService;
  typeSearchValue: string = "";
  typeIdValue: string = "";
  typeSelectedValue: { displayId: string; id: string } | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadMusicalWorkLinkById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.musicalWorkLinkOne({ id });
    this.musicalWorkLinkInstance = result.musicalWorkLinkOne;
    if (this.musicalWorkLinkInstance) {
      this.loadValues(this.musicalWorkLinkInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.musicalWorkLinkInstance) {
      return musicalWorkLinkDisplayId(
        this.musicalWorkLinkInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.MusicalWorkLinkSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "platform") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.platformOne({
          id: params.returnData.resultId,
        });
        const platform = result.platformOne;
        this.platformSelectedValue = platform;
      } else if (params.callerData?.fieldName === "type") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.platformLinkTypeOne({
          id: params.returnData.resultId,
        });
        const platformLinkType = result.platformLinkTypeOne;
        this.typeSelectedValue = platformLinkType;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get platformValue() {
    if (this.platformSelectedValue) {
      return {
        id: this.platformSelectedValue.id,
        displayId: platformDisplayId(
          this.platformSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get typeValue() {
    if (this.typeSelectedValue) {
      return {
        id: this.typeSelectedValue.id,
        displayId: platformLinkTypeDisplayId(
          this.typeSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.musicalWorkLinkInstance = null;
    this.urlValue = "";
    this.platformSearchValue = "";
    this.platformIdValue = "";
    this.typeSearchValue = "";
    this.typeIdValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelMusicalWorkLink() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editMusicalWorkLink() {
    const id = this.musicalWorkLinkInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/musicalWorkLink/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printMusicalWorkLink(handlePrint: () => void): void {
    handlePrint();
  }

  *closeMusicalWorkLink(): any {
    yield this.close();
  }

  loadValues(instance: MusicalWorkLink) {
    this.idValue = instance.id;
    this.urlValue = instance.url;
    this.platformSelectedValue = instance.platform ? instance.platform : null;
    this.typeSelectedValue = instance.type ? instance.type : null;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadMusicalWorkLinkById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
